import {
	json,
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { z } from 'zod'
import { WorkshopHero } from '#app/ui/workshop/hero.tsx'
import { MailForm } from '#app/ui/workshop/mail-form.tsx'
import { ReserveSeat } from '#app/ui/workshop/reserve-seat.tsx'
import {
	appName,
	stripeCheckoutUrl,
	supportEmail,
	workshopPrice,
} from '#app/utils/constants.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { getDomainUrl } from '#app/utils/misc.tsx'
import { useNonce } from '#app/utils/nonce-provider.ts'

const MailSchema = z.object({
	email: z
		.string({ required_error: 'Enter an email' })
		.email('Enter a valid email'),
})

const mapsHq =
	'https://www.google.com/maps/dir/?api=1&destination=' +
	encodeURIComponent('Rua Padre Américo 10, Odivelas, Portugal')

const jsonLd = JSON.stringify({
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'EducationEvent',
			name: 'An AI that already knows you',
			description:
				'Sit down and put it to work. Do not brief a stranger first. In-person workshop with André Casal.',
			eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
			eventStatus: 'https://schema.org/EventScheduled',
			location: {
				'@type': 'Place',
				name: 'Lisbon',
				address: {
					'@type': 'PostalAddress',
					addressLocality: 'Lisbon',
					addressCountry: 'PT',
				},
			},
			organizer: {
				'@type': 'Person',
				name: 'André Casal',
				email: supportEmail,
			},
			offers: {
				'@type': 'Offer',
				price: '30',
				priceCurrency: 'EUR',
				url: stripeCheckoutUrl,
				availability: 'https://schema.org/InStock',
			},
		},
		{
			'@type': 'Organization',
			name: 'Casal Innovations',
			email: supportEmail,
			address: {
				'@type': 'PostalAddress',
				streetAddress: 'Rua Padre Américo 10',
				addressLocality: 'Odivelas',
				addressCountry: 'PT',
			},
		},
	],
})

export async function loader({ request }: LoaderFunctionArgs) {
	return json({ origin: getDomainUrl(request) })
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	const origin = data?.origin ?? ''
	const title = 'An AI that already knows you'
	const description =
		'Sit down and put it to work. Do not brief a stranger first. In-person workshop with André Casal.'
	const image = `${origin}/img/workshop/og.jpg`
	return [
		{ title },
		{ name: 'description', content: description },
		{ name: 'author', content: 'André Casal' },
		{ name: 'theme-color', content: '#F2ECE0' },
		{ property: 'og:title', content: appName },
		{ property: 'og:description', content: description },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:image', content: image },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		{ name: 'twitter:image', content: image },
	]
}

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	checkHoneypot(formData)

	const parsed = MailSchema.safeParse({
		email: String(formData.get('email') ?? ''),
	})
	if (!parsed.success) {
		return json(
			{ ok: false as const, error: 'Enter a valid email' },
			{ status: 400 },
		)
	}

	// Stored later via Resend. Accept the address for now.
	return json({ ok: true as const })
}

export default function Index() {
	const nonce = useNonce()

	return (
		<div className="bg-cream font-sans text-ink">
			<script
				nonce={nonce}
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: jsonLd }}
			/>
			<main id="top">
				<WorkshopHero />

				<section
					id="about"
					className="grid gap-10 px-6 py-20 md:grid-cols-2 md:items-center md:gap-16 md:px-12 md:py-28"
				>
					<div>
						<h2 className="max-w-[12ch] text-4xl font-semibold leading-none tracking-tighter md:text-6xl">
							A folder that remembers.
						</h2>
						<p className="mt-6 max-w-[65ch] text-base leading-relaxed text-ink-soft">
							A new chat is a stranger. It does not know your name, your rules,
							or your work. This workshop is how you point a command-line AI at
							one personal folder, so the next session already knows you.
						</p>
						<p className="mt-4 max-w-[65ch] text-base leading-relaxed text-ink-soft">
							André Casal runs it in person. Mixed room. No programming
							assumed. You leave with the folder, two skills, and one real job
							done: plan tomorrow, and draft a message you would actually send.
						</p>
					</div>
					<figure className="border border-rule">
						<img
							src="/img/workshop/archive.jpg"
							alt="Cream folders, a navy notebook, and a fountain pen on oak"
							className="aspect-[4/3] w-full object-cover"
							width={1600}
							height={1200}
						/>
					</figure>
				</section>

				<section
					id="workshop"
					className="border-t border-rule px-6 py-20 md:px-12 md:py-28"
				>
					<h2 className="max-w-[16ch] text-4xl font-semibold leading-none tracking-tighter md:text-6xl">
						What you will do.
					</h2>
					<ol className="mt-12 divide-y divide-rule border-y border-rule">
						{[
							{
								n: '01',
								title: 'Install',
								body: 'A command-line AI on your laptop, pointed at one folder. VS Code if you need it. Cursor or Windsurf already counts.',
							},
							{
								n: '02',
								title: 'Write',
								body: 'Who you are and the rules it must follow. Then prove it answers with your name, from the file.',
							},
							{
								n: '03',
								title: 'Add',
								body: 'A skill so the right recipe loads for a job. Say plan tomorrow. Say draft a WhatsApp. Watch it read the matching file.',
							},
							{
								n: '04',
								title: 'Run',
								body: 'Fill notes, calendar, and mail. Leave with a tomorrow plan on screen and a draft you would send.',
							},
						].map((item) => (
							<li
								key={item.n}
								className="grid gap-3 py-8 md:grid-cols-[5rem_12rem_1fr] md:items-baseline md:gap-8"
							>
								<span className="font-mono text-xs uppercase tracking-[0.18em] text-terracotta">
									{item.n}
								</span>
								<h3 className="text-2xl font-semibold tracking-tight">
									{item.title}
								</h3>
								<p className="max-w-[65ch] text-base leading-relaxed text-ink-soft">
									{item.body}
								</p>
							</li>
						))}
					</ol>
					<figure className="mt-16 border border-rule">
						<img
							src="/img/workshop/workshop.jpg"
							alt="An in-person workshop table with open laptops and paper"
							className="aspect-[21/9] w-full object-cover"
							width={2000}
							height={857}
						/>
					</figure>
				</section>

				<section
					id="andre"
					className="grid gap-10 border-t border-rule px-6 py-20 md:grid-cols-2 md:items-center md:gap-16 md:px-12 md:py-28"
				>
					<figure className="border border-rule md:order-first">
						<img
							src="/img/workshop/andre.png"
							alt="André Casal"
							className="aspect-square w-full object-cover object-center"
							width={600}
							height={600}
						/>
					</figure>
					<div>
						<p className="font-mono text-xs uppercase tracking-[0.16em] text-muted-500">
							The person in the room
						</p>
						<h2 className="mt-4 max-w-[14ch] text-4xl font-semibold leading-none tracking-tighter md:text-6xl">
							I'm André. I'll be the one teaching it.
						</h2>
						<p className="mt-6 max-w-[65ch] text-base leading-relaxed text-ink-soft">
							Engineer. Twenty years building software. More than eight hundred
							students. Work for Microsoft, NBC News, and the Gulbenkian
							Foundation. I think from first principles, then I build.
						</p>
						<p className="mt-4 max-w-[65ch] text-base leading-relaxed text-ink-soft">
							I have spent those years making hard things make sense to people
							who did not come from programming. Mixed room. No jargon first. If
							an explanation fails, I find another until it holds.
						</p>
						<p className="mt-4 max-w-[65ch] text-base leading-relaxed text-ink-soft">
							I run Casal Innovations in Lisbon. Small on purpose. When you are
							in the room, you are with the person who has to live with what he
							taught you.
						</p>
						<dl className="mt-10 grid grid-cols-2 gap-8 border-t border-rule pt-8">
							<div>
								<dt className="font-mono text-xs uppercase tracking-[0.16em] text-muted-500">
									Years building software
								</dt>
								<dd className="mt-2 text-3xl font-semibold tracking-tight">
									20+
								</dd>
							</div>
							<div>
								<dt className="font-mono text-xs uppercase tracking-[0.16em] text-muted-500">
									Students taught
								</dt>
								<dd className="mt-2 text-3xl font-semibold tracking-tight">
									800+
								</dd>
							</div>
						</dl>
					</div>
				</section>

				<section
					id="reviews"
					className="bg-navy px-6 py-20 text-cream md:px-12 md:py-28"
				>
					<h2 className="max-w-[12ch] text-4xl font-semibold leading-none tracking-tighter md:text-6xl">
						From the first room.
					</h2>
					<p className="mt-4 max-w-[50ch] font-mono text-xs uppercase tracking-[0.14em] text-cream/60">
						Reviews translated from Portuguese
					</p>
					<ul className="mt-12 space-y-12">
						<li className="max-w-3xl border-t border-cream/20 pt-8">
							<blockquote className="text-xl leading-snug tracking-tight md:text-2xl">
								“For me, it was seeing Grok's potential to automate tasks that
								are part of running my business every day. I found it especially
								interesting to use it for social media management, like
								Instagram and WhatsApp, for creating and organizing content, and
								for automating communication. It made me realize I can save a
								lot of time and make managing my projects much more efficient.”
							</blockquote>
							<p className="mt-5 font-mono text-xs uppercase tracking-[0.16em] text-terracotta">
								Lúcia Gonçalves · 9.5 / 10
							</p>
						</li>
						<li className="max-w-3xl border-t border-cream/20 pt-8">
							<blockquote className="text-xl leading-snug tracking-tight md:text-2xl">
								“I loved the idea of a portable, tool-agnostic AI instruction
								repo. I also loved the possibility of having several of them for
								different profiles.”
							</blockquote>
							<p className="mt-5 font-mono text-xs uppercase tracking-[0.16em] text-terracotta">
								João · 9 / 10
							</p>
						</li>
						<li className="max-w-3xl border-t border-cream/20 pt-8">
							<blockquote className="text-xl leading-snug tracking-tight md:text-2xl">
								“The underlying knowledge of using AI as a tool.”
							</blockquote>
							<p className="mt-5 font-mono text-xs uppercase tracking-[0.16em] text-terracotta">
								João · 9 / 10
							</p>
						</li>
					</ul>
				</section>

				<section
					id="next"
					className="grid gap-12 px-6 py-20 md:grid-cols-2 md:px-12 md:py-28"
				>
					<div>
						<h2 className="text-4xl font-semibold leading-none tracking-tighter md:text-6xl">
							Next session.
						</h2>
						<p className="mt-6 max-w-[65ch] text-base leading-relaxed text-ink-soft">
							Date, time, and venue are still being set. Reserve a seat and we
							will write with the details. Bring a laptop you can install on,
							and a paid terminal AI login.
						</p>
						<p className="mt-6 text-2xl font-semibold tracking-tight">
							{workshopPrice} per person
						</p>
						<div className="mt-8">
							<ReserveSeat />
						</div>
					</div>
					<dl className="divide-y divide-rule border-y border-rule">
						<div className="grid gap-2 py-6 md:grid-cols-2">
							<dt className="font-mono text-xs uppercase tracking-[0.16em] text-muted-500">
								Hours
							</dt>
							<dd className="text-base leading-relaxed">
								1.5 hours, then a 30-minute break, then 1.5 hours. Dinner after.
								Exact clock time to be announced.
							</dd>
						</div>
						<div className="grid gap-2 py-6 md:grid-cols-2">
							<dt className="font-mono text-xs uppercase tracking-[0.16em] text-muted-500">
								Location
							</dt>
							<dd className="text-base leading-relaxed">
								Lisbon. Exact venue sent to people who reserve.
							</dd>
						</div>
						<div className="grid gap-2 py-6 md:grid-cols-2">
							<dt className="font-mono text-xs uppercase tracking-[0.16em] text-muted-500">
								Bring
							</dt>
							<dd className="text-base leading-relaxed">
								Laptop and charger. A terminal AI subscription: Claude Code,
								Codex CLI, Grok, Gemini CLI, or Copilot CLI. The browser app is
								not enough.
							</dd>
						</div>
					</dl>
				</section>
			</main>

			<footer id="list" className="bg-navy px-6 py-16 text-cream md:px-12 md:py-20">
				<div className="grid gap-12 md:grid-cols-2">
					<div>
						<h2 className="text-3xl font-semibold leading-none tracking-tighter md:text-4xl">
							Stay on the list.
						</h2>
						<p className="mt-4 max-w-[40ch] text-base leading-relaxed text-cream/75">
							Session dates and high-leverage skills for automating your own
							work.
						</p>
						<div className="mt-8">
							<MailForm />
						</div>
					</div>
					<div className="md:justify-self-end">
						<p className="font-mono text-xs uppercase tracking-[0.16em] text-cream/60">
							Contact
						</p>
						<p className="mt-3 text-lg font-medium">André Casal</p>
						<p className="text-cream/75">Casal Innovations, Lda</p>
						<a
							href={`mailto:${supportEmail}`}
							className="mt-4 block text-cream underline underline-offset-4"
						>
							{supportEmail}
						</a>
						<a
							href={mapsHq}
							target="_blank"
							rel="noreferrer"
							className="mt-6 block max-w-[28ch] text-base leading-relaxed text-cream/80 underline-offset-4 hover:underline"
						>
							Rua Padre Américo 10
							<br />
							Odivelas, Portugal
						</a>
					</div>
				</div>
				<p className="mt-16 font-mono text-[11px] uppercase tracking-[0.14em] text-cream/40">
					Casal Innovations · Lisbon
				</p>
			</footer>
		</div>
	)
}
